import { useEffect, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { paths } from 'src/routes/paths';

import { useNetworkStatus } from 'src/hooks/use-network-status';

// ----------------------------------------------------------------------

type Props = {
  children: ReactNode;
};

export default function NetworkGuard({ children }: Props) {
  const { isOnline } = useNetworkStatus();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Don't redirect if already on offline page or system pages that should still work offline
    const isOnOfflinePage = location.pathname === paths.system.offline;
    const isOnSystemPage = location.pathname.startsWith('/system/');
    const isOnAuthPage = location.pathname.startsWith('/auth/');

    if (!isOnline && !isOnOfflinePage && !isOnSystemPage && !isOnAuthPage) {
      // Store the current location to redirect back after reconnection
      sessionStorage.setItem('preOfflineLocation', location.pathname + location.search);
      navigate(paths.system.offline, { replace: true });
    } else if (isOnline && isOnOfflinePage) {
      // When connection is restored and user is on offline page, redirect back.
      // Use a hard reload (window.location.replace) instead of React Router navigate:
      // Firestore's AsyncQueue can enter a fatal error state during network loss,
      // and a soft navigation doesn't reinitialize the singleton — only a full page
      // reload does.
      const previousLocation = sessionStorage.getItem('preOfflineLocation');
      sessionStorage.removeItem('preOfflineLocation');
      window.location.replace(previousLocation || paths.dashboard.root);
    }
  }, [isOnline, location.pathname, location.search, navigate]);

  return <>{children}</>;
}
